import React, { useRef } from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useReducedMotion } from "framer-motion";
import { useLandingTheme } from "../LandingThemeContext";
import { useLandingCopy } from "../LandingLanguageContext";
import { ContentContainer, SectionHeading } from "./shared";
import { DeferredImage } from "../visuals/DeferredImage";
import { useIsNearViewport } from "../visuals/useIsNearViewport";

// Colours lifted from the real call screen (src/voice/react/VoiceCallScreen.tsx, the "Sage / butter
// circle" design) and the entry pill (CallMonkeyEntryPill.tsx), so the mock reads as the product.
const CALL_SCREEN = {
  bgGradient: "linear-gradient(180deg, #D5DED9 0%, #EDF7F1 100%)",
  butter: "#F9FFD4",
  ink: "#121212",
  inkMuted: "rgba(18, 18, 18, 0.55)",
  deepGreen: "#003D16",
  green: "#005720",
  endRed: "#FF3B30",
  userBubbleBg: "#E4FFD1",
  monkeyBubbleBg: "rgba(255, 255, 255, 0.95)",
  artifactCardBg: "rgba(255,255,255,0.95)",
  artifactCaption: "#696A69",
  glowListening: "163, 217, 0",
  glowSpeaking: "176, 108, 44",
  font: '"Outfit", sans-serif',
};

// The avatar's glow is a live waveform in the app; here only the ring breathes gently while
// "monkey" talks — the avatar itself stays still.
const monkeyGlow = keyframes`
  0%, 100% { box-shadow: 0 0 22px rgba(${CALL_SCREEN.glowSpeaking}, 0.45), 0 0 40px rgba(${CALL_SCREEN.glowSpeaking}, 0.22); }
  50% { box-shadow: 0 0 34px rgba(${CALL_SCREEN.glowSpeaking}, 0.5), 0 0 58px rgba(${CALL_SCREEN.glowSpeaking}, 0.26); }
`;

function HangupIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9c-.98.49-1.87 1.12-2.66 1.85c-.18.18-.43.28-.7.28c-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71c0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29c-.27 0-.52-.11-.7-.28a11.27 11.27 0 0 0-2.66-1.85a.99.99 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9Z" fill={color} />
    </svg>
  );
}

function MicIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3m7 9c0 3.53-2.61 6.43-6 6.92V21h-2v-3.08c-3.39-.49-6-3.39-6-6.92h2a5 5 0 0 0 5 5a5 5 0 0 0 5-5z" fill={color} />
    </svg>
  );
}

// An image artifact as the app's ArtifactCard draws it: the standard light card, a small caption
// label, and the picture the tutor pulled up while talking. The picture is far below the fold, so it
// loads once the card comes near rather than with the first paint. Its 96px height leaves room for
// the complete conversation even in editions whose messages need more lines.
function ImageArtifactCard({ caption, src, alt }: { caption: string; src: string; alt: string }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isCardNearViewport = useIsNearViewport(cardRef);
  return (
    <Box ref={cardRef} w="100%" borderRadius="14px" px="14px" py="10px" bg={CALL_SCREEN.artifactCardBg}>
      <Text fontSize="12px" fontWeight={600} color={CALL_SCREEN.artifactCaption} mb="8px" pl="2px">
        {caption}
      </Text>
      <DeferredImage shouldLoad={isCardNearViewport} fileWidth={420} fileHeight={560} src={src} alt={alt} borderRadius="8px" w="100%" h="96px" objectFit="cover" objectPosition="center 35%" />
    </Box>
  );
}

function Bubble({ fromUser, children, opacity = 1 }: { fromUser: boolean; children: React.ReactNode; opacity?: number }) {
  return (
    <Flex justify={fromUser ? "flex-end" : "flex-start"} w="100%" opacity={opacity}>
      <Box maxW={fromUser ? "78%" : "92%"} borderRadius={fromUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px"} px="16px" py="10px" fontSize="14px" lineHeight={1.4} color={CALL_SCREEN.ink} bg={fromUser ? CALL_SCREEN.userBubbleBg : CALL_SCREEN.monkeyBubbleBg}>
        {children}
      </Box>
    </Flex>
  );
}

// The voice call screen as the app draws it, minus its top-bar pills (detail the landing page does not
// need): sage gradient, the glowing monkey avatar, the spoken bubbles, and the mute / end controls.
// The mock is drawn at the call screen's real size and, on a phone, shown scaled down as a whole (a
// narrower box at full size would re-wrap the bubbles and stay just as tall): about a tenth of the screen
// narrower than the text column, so the whole call fits on one screen. The wrapper takes the scaled size,
// because a transform does not change the space an element occupies.
const CALL_SCREEN_WIDTH_PIXELS = 342;
const CALL_SCREEN_HEIGHT_PIXELS = 724;
const CALL_SCREEN_PHONE_SCALE = 0.86;
const CALL_SCREEN_NARROW_PHONE_SCALE = 0.72;
const NARROW_PHONE_MEDIA_QUERY = "@media (max-width: 359px)";

function CallScreenMock() {
  return (
    <Box
      data-landing-call-preview
      mx="auto"
      flexShrink={0}
      w={{ base: `${CALL_SCREEN_WIDTH_PIXELS * CALL_SCREEN_PHONE_SCALE}px`, md: `${CALL_SCREEN_WIDTH_PIXELS}px` }}
      h={{ base: `${CALL_SCREEN_HEIGHT_PIXELS * CALL_SCREEN_PHONE_SCALE}px`, md: `${CALL_SCREEN_HEIGHT_PIXELS}px` }}
      sx={{
        [NARROW_PHONE_MEDIA_QUERY]: {
          width: `${CALL_SCREEN_WIDTH_PIXELS * CALL_SCREEN_NARROW_PHONE_SCALE}px`,
          height: `${CALL_SCREEN_HEIGHT_PIXELS * CALL_SCREEN_NARROW_PHONE_SCALE}px`,
        },
      }}
    >
      {/* Scale the full canvas around its inline start so RTL keeps the preview inside its wrapper. */}
      <Box
        w={`${CALL_SCREEN_WIDTH_PIXELS}px`}
        transformOrigin="top left"
        _rtl={{ transformOrigin: "top right" }}
        transform={{ base: `scale(${CALL_SCREEN_PHONE_SCALE})`, md: "none" }}
        sx={{ [NARROW_PHONE_MEDIA_QUERY]: { transform: `scale(${CALL_SCREEN_NARROW_PHONE_SCALE})` } }}
      >
        <CallScreenAtFullSize />
      </Box>
    </Box>
  );
}

function CallScreenAtFullSize() {
  const reduceMotion = useReducedMotion();
  const callScreenCopy = useLandingCopy().learnByTalking.callScreen;
  return (
    <Box w={`${CALL_SCREEN_WIDTH_PIXELS}px`} borderRadius="24px" overflow="hidden" boxShadow="0 24px 60px rgba(0, 40, 20, 0.18)" background={CALL_SCREEN.bgGradient} fontFamily={CALL_SCREEN.font} display="flex" flexDirection="column" h={`${CALL_SCREEN_HEIGHT_PIXELS}px`}>
      <Flex direction="column" align="center" pt="28px" pb="14px" flexShrink={0}>
        <Box position="relative" w="115px" h="115px" display="flex" alignItems="center" justifyContent="center">
          <Box position="absolute" w="205px" h="205px" borderRadius="999px" background={`radial-gradient(circle, rgba(${CALL_SCREEN.glowSpeaking}, 0.3) 0%, transparent 70%)`} opacity={0.7} />
          <Box w="115px" h="115px" borderRadius="999px" overflow="hidden" bg={CALL_SCREEN.butter} boxShadow={`0 0 22px rgba(${CALL_SCREEN.glowSpeaking}, 0.45), 0 0 40px rgba(${CALL_SCREEN.glowSpeaking}, 0.22)`} animation={reduceMotion ? "none" : `${monkeyGlow} 3.2s ease-in-out infinite`}>
            <Image src="/images/landing/inventory/monkey-doctor.svg" alt="" w="100%" h="100%" objectFit="cover" />
          </Box>
        </Box>
        <Text fontSize="18px" fontWeight={700} color={CALL_SCREEN.ink} mt="10px">
          {callScreenCopy.tutorName}
        </Text>
        <Text fontSize="12px" color={CALL_SCREEN.inkMuted} mt="2px">
          02:14
        </Text>
      </Flex>

      <Flex data-landing-call-messages direction="column" gap="10px" px="18px" flex="1" minH={0} overflow="hidden" justify="flex-end" pb="8px">
        <Bubble fromUser>{callScreenCopy.learnerRequestBubble}</Bubble>
        <Bubble fromUser={false}>
          {callScreenCopy.tutorExplanationBubble}
        </Bubble>
        <ImageArtifactCard caption={callScreenCopy.slideImageCaption} src="/images/landing/slides/villi-diagram.webp" alt={callScreenCopy.slideImageAlt} />
        <Bubble fromUser={false}>{callScreenCopy.tutorQuestionBubble}</Bubble>
      </Flex>

      <Flex justify="center" gap="28px" pt="20px" pb="36px" flexShrink={0}>
        <Flex direction="column" align="center" gap="6px">
          <Flex w="50px" h="50px" borderRadius="999px" bg="#FFFFFF" align="center" justify="center" boxShadow="0 2px 10px rgba(40, 60, 50, 0.10)">
            <MicIcon size={22} color={CALL_SCREEN.green} />
          </Flex>
          <Text fontSize="11px" fontWeight={500} color={CALL_SCREEN.green}>{callScreenCopy.muteButtonLabel}</Text>
        </Flex>
        <Flex direction="column" align="center" gap="6px">
          <Flex w="50px" h="50px" borderRadius="999px" bg={CALL_SCREEN.endRed} align="center" justify="center" boxShadow="0 4px 14px rgba(0, 0, 0, 0.13)">
            <HangupIcon size={22} color="#FFFFFF" />
          </Flex>
          <Text fontSize="11px" fontWeight={500} color={CALL_SCREEN.green}>{callScreenCopy.endCallButtonLabel}</Text>
        </Flex>
      </Flex>
    </Box>
  );
}

// Voice on the landing page: the two ways a call starts — the learner calls monkey, or monkey
// puts the learner on the spot — beside the call screen itself.
export default function LearnByTalkingSection() {
  const theme = useLandingTheme();
  const learnByTalkingCopy = useLandingCopy().learnByTalking;
  return (
    <Box as="section" pt={{ base: "120px", md: "140px", lg: "160px" }}>
      <ContentContainer>
        <Flex direction={{ base: "column", lg: "row" }} align="center" justify="space-between" gap={{ base: "36px", lg: "64px" }} px={{ lg: "16px" }}>
          <Box maxW={{ base: "100%", lg: "460px" }} w="100%">
            <SectionHeading textAlign={{ base: "center", lg: "left" }} fontSize={{ base: "24px", lg: `${Math.round(37 * theme.heading.sizeScale)}px` }}>
              {learnByTalkingCopy.heading}
            </SectionHeading>

            <Flex direction="column" gap={{ base: "4px", lg: "18px" }} mt={{ base: "20px", lg: "36px" }} align={{ base: "center", lg: "flex-start" }}>
              <Text fontFamily={theme.font.body} fontSize={{ base: "18px", lg: "20px" }} fontWeight={500} color={theme.color.text} textAlign={{ base: "center", lg: "left" }} sx={{ textWrap: "balance" }}>
                {learnByTalkingCopy.callMonkeyTutorLine}
              </Text>
              <Text fontFamily={theme.font.body} fontSize={{ base: "18px", lg: "20px" }} fontWeight={500} color={theme.color.text} textAlign={{ base: "center", lg: "left" }} sx={{ textWrap: "balance" }}>
                {learnByTalkingCopy.monkeyCallsYouLine}
              </Text>
            </Flex>
          </Box>

          <CallScreenMock />
        </Flex>
      </ContentContainer>
    </Box>
  );
}
